"use client"

import {
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/shad"
import { cn } from "@ui/utils"
import { format } from "date-fns"
import { X } from "lucide-react"

import {
  MONTHLY_RULE_TYPE_OPTIONS,
  WEEKDAY_OPTIONS,
  WEEKDAY_ORDINAL_OPTIONS,
  type MonthlyRuleType,
  type Weekday,
  type WeekdayOrdinal,
} from "@/components/tasks/utils/monthly-recurrence"

const INTERVAL_VALUES = Array.from({ length: 12 }, (_, index) => String(index + 1))
const UNIT_OPTIONS = [
  { value: "days", label: "day", pluralLabel: "days" },
  { value: "weeks", label: "week", pluralLabel: "weeks" },
  { value: "months", label: "month", pluralLabel: "months" },
  { value: "years", label: "year", pluralLabel: "years" },
] as const

type IntervalUnit = (typeof UNIT_OPTIONS)[number]["value"]

type RecurrenceUpdates = Partial<{
  enabled: boolean
  intervalValue: number | null
  intervalUnit: IntervalUnit | null
  monthlyRuleType: MonthlyRuleType | null
  weekday: Weekday | null
  weekdayOrdinal: WeekdayOrdinal | null
  startDate?: Date | null
  endDate?: Date | null
}>

interface TaskCreateRecurrenceSelectorProps {
  enabled: boolean
  intervalValue: number | null
  intervalUnit: IntervalUnit | null
  monthlyRuleType?: MonthlyRuleType | null
  weekday?: Weekday | null
  weekdayOrdinal?: WeekdayOrdinal | null
  startDate?: Date
  endDate?: Date
  onChange: (updates: RecurrenceUpdates) => void
  onClear: () => void
  className?: string
}

export default function TaskCreateRecurrenceSelector({
  enabled,
  intervalValue,
  intervalUnit,
  monthlyRuleType = "calendar_day",
  weekday = null,
  weekdayOrdinal = null,
  startDate,
  endDate,
  onChange,
  onClear,
  className,
}: TaskCreateRecurrenceSelectorProps) {
  if (!enabled) {
    return null
  }

  const normalizedToday = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
  }

  const maybeAddDefaultStartDate = (updates: RecurrenceUpdates) => {
    const nextIntervalValue = updates.intervalValue ?? intervalValue
    const nextIntervalUnit = updates.intervalUnit ?? intervalUnit

    if (!startDate && nextIntervalValue != null && nextIntervalUnit) {
      updates.startDate = normalizedToday()
    }

    return updates
  }

  const handleIntervalValueChange = (value: string) => {
    const updates = maybeAddDefaultStartDate({ intervalValue: Number(value), enabled: true })
    onChange(updates)
  }

  const handleIntervalUnitChange = (value: string) => {
    const nextUnit = value as IntervalUnit
    const updates = maybeAddDefaultStartDate({
      intervalUnit: nextUnit,
      ...(nextUnit === "years" ? { intervalValue: Math.min(intervalValue ?? 1, 2) } : {}),
      enabled: true,
      ...(nextUnit === "months" || (nextUnit === "years" && monthlyRuleType === "last_day")
        ? {
            monthlyRuleType: monthlyRuleType ?? "calendar_day",
          }
        : {
            monthlyRuleType: "calendar_day",
            weekday: null,
            weekdayOrdinal: null,
          }),
    })
    onChange(updates)
  }

  const handleMonthlyRuleTypeChange = (value: string) => {
    const nextType = value as MonthlyRuleType
    if (nextType === "day_of_week") {
      onChange({
        monthlyRuleType: nextType,
        weekday: weekday ?? "monday",
        weekdayOrdinal: weekdayOrdinal ?? "first",
        enabled: true,
      })
      return
    }

    onChange({
      monthlyRuleType: nextType,
      weekday: null,
      weekdayOrdinal: null,
      enabled: true,
    })
  }

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) {
      onChange({ startDate: null, enabled: true })
      return
    }

    const normalized = new Date(date)
    normalized.setHours(0, 0, 0, 0)
    onChange({ startDate: normalized, enabled: true })
  }

  const handleEndDateSelect = (date: Date | undefined) => {
    if (!date) {
      onChange({ endDate: null, enabled: true })
      return
    }

    const normalized = new Date(date)
    normalized.setHours(0, 0, 0, 0)
    onChange({ endDate: normalized, enabled: true })
  }

  const handleEndDateClear = () => {
    onChange({ endDate: null, enabled: true })
  }

  const disableEndDateBeforeStart = (date: Date) => {
    if (!startDate) return false
    const start = new Date(startDate)
    start.setHours(0, 0, 0, 0)
    return date < start
  }

  const handleClear = () => {
    onChange({
      intervalValue: null,
      intervalUnit: null,
      monthlyRuleType: "calendar_day",
      weekday: null,
      weekdayOrdinal: null,
      startDate: null,
      endDate: null,
      enabled: false,
    })
    onClear()
  }

  const showMonthlyOn = intervalUnit === "months"
  const showWeekdayFields = showMonthlyOn && monthlyRuleType === "day_of_week"

  return (
    <div className={cn("flex flex-col gap-2 text-sm", className)}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-foreground text-sm font-medium">First due</span>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 min-w-[132px] justify-between px-3"
              type="button"
              aria-label="Select the first due date"
            >
              <span className={cn("text-sm font-medium", !startDate && "text-muted-foreground")}>
                {startDate ? format(startDate, "MM/dd/yyyy") : "Select date"}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={startDate} onSelect={handleDateSelect} initialFocus />
          </PopoverContent>
        </Popover>
        <span className="text-foreground text-sm font-medium">repeats every</span>
        <Select
          value={intervalValue != null ? String(intervalValue) : undefined}
          onValueChange={handleIntervalValueChange}
        >
          <SelectTrigger className="h-8 w-16 justify-between" aria-label="Select repeat interval value">
            <SelectValue placeholder="-" />
          </SelectTrigger>
          <SelectContent className="max-h-60">
            {(intervalUnit === "years" ? INTERVAL_VALUES.slice(0, 2) : INTERVAL_VALUES).map((value) => (
              <SelectItem key={value} value={value}>
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={intervalUnit ?? undefined} onValueChange={handleIntervalUnitChange}>
          <SelectTrigger className="h-8 w-[108px] justify-between" aria-label="Select repeat interval unit">
            <SelectValue placeholder="-" />
          </SelectTrigger>
          <SelectContent>
            {UNIT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {intervalValue === 1 ? option.label : option.pluralLabel}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-foreground text-sm font-medium">until</span>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 min-w-[132px] justify-between px-3"
              type="button"
              aria-label="Select the end date (optional)"
            >
              <span className={cn("text-sm font-medium", !endDate && "text-muted-foreground")}>
                {endDate ? format(endDate, "MM/dd/yyyy") : "Never"}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={endDate}
              onSelect={handleEndDateSelect}
              disabled={disableEndDateBeforeStart}
              initialFocus
            />
            {endDate && (
              <div className="border-t p-2">
                <Button variant="ghost" size="sm" type="button" className="w-full" onClick={handleEndDateClear}>
                  Clear end date
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
        <Button
          variant="ghost"
          size="iconXs"
          type="button"
          onClick={handleClear}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Remove repeating"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {showMonthlyOn && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-foreground text-sm font-medium">On</span>
          <Select value={monthlyRuleType ?? "calendar_day"} onValueChange={handleMonthlyRuleTypeChange}>
            <SelectTrigger className="h-8 w-[140px] justify-between" aria-label="Select monthly rule type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHLY_RULE_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {showWeekdayFields && (
            <>
              <Select
                value={weekdayOrdinal ?? "first"}
                onValueChange={(value) => onChange({ weekdayOrdinal: value as WeekdayOrdinal, enabled: true })}
              >
                <SelectTrigger className="h-8 w-[110px] justify-between" aria-label="Select weekday ordinal">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WEEKDAY_ORDINAL_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={weekday ?? "monday"}
                onValueChange={(value) => onChange({ weekday: value as Weekday, enabled: true })}
              >
                <SelectTrigger className="h-8 w-[130px] justify-between" aria-label="Select weekday">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WEEKDAY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
        </div>
      )}
    </div>
  )
}
